import { FunctionComponent, useCallback, useRef, useState } from 'react';
import { useFeatureRollout } from '@rea-argonaut/experiment';
import {
  MultiIntentSearchModal,
  SearchFlowType,
  MOBILE_MAX,
  useHashTrigger,
} from '@property.com.au/ui-kit';
import { replace } from '@rea-argonaut/link';
import { trackModalImpression } from '../../../analytics/event-tracking/trackModalImpression';
import { getEnv } from '../../../helpers/getEnv';

const PROPERTY_PAGE_TRACKING_SOURCE = 'property-page';

export const PropertySearch: FunctionComponent<{ isSignedIn: boolean }> = ({ isSignedIn }) => {
  const [isMultiIntentSearchModalOpen, setIsMultiIntentSearchModalOpen] = useState(false);
  const [initialSearchFlow, setInitialSearchFlow] = useState<SearchFlowType>('LocationSearch');

  const openCommuteSearch = useCallback(() => {
    setInitialSearchFlow('CommuteSearch');
    setIsMultiIntentSearchModalOpen(true);
  }, []);

  const openLocationSearch = useCallback(() => {
    setInitialSearchFlow('LocationSearch');
    setIsMultiIntentSearchModalOpen(true);
  }, []);

  const closeMultiIntentSearchModal = useCallback(() => {
    setIsMultiIntentSearchModalOpen(false);
  }, []);

  const buttonRef = useCallback((node: HTMLButtonElement | null) => {
    if (node) {
      multiIntentSearchModalButtonRef.current = node;
    }
  }, []);

  const multiIntentSearchModalButtonRef = useRef<HTMLButtonElement | null>(null);
  const { isEnabled: isCommuteSearchEnabled } = useFeatureRollout('commute_search');

  useHashTrigger(
    {
      '#search-traveltime': () => {
        openCommuteSearch();
        trackModalImpression({
          modal_name: 'multi_intent',
          screen_name: 'commute_search_point_of_interest_search_screen',
          in_page_source: 'url-fragment:travel-time',
          modal_is_fullscreen: window.innerWidth <= MOBILE_MAX,
        });
      },
      '#search-default': () => {
        openLocationSearch();
        trackModalImpression({
          modal_name: 'multi_intent',
          screen_name: 'location_search_screen',
          in_page_source: 'url-fragment:search-default',
          modal_is_fullscreen: window.innerWidth <= MOBILE_MAX,
        });
      },
    },
    {
      onHashHandled: () => {
        replace(location.pathname + location.search);
      },
    },
  );

  return (
    <>
      <button ref={buttonRef} style={{ display: 'none' }} />
      {multiIntentSearchModalButtonRef.current && (
        <MultiIntentSearchModal
          opened={isMultiIntentSearchModalOpen}
          onClose={closeMultiIntentSearchModal}
          pageSource={PROPERTY_PAGE_TRACKING_SOURCE}
          data-testid="property-page-multi-intent-search-modal"
          buttonRef={multiIntentSearchModalButtonRef as React.RefObject<HTMLButtonElement>}
          isSignedIn={isSignedIn}
          publicExperienceApiUrl={getEnv.PUBLIC_EXPERIENCE_API_URL()}
          rootElementId="argonaut-wrapper"
          googleApiKey={getEnv.GOOGLE_MAP_API_KEY()}
          enableCommuteSearch={isCommuteSearchEnabled()}
          initialSearchFlow={initialSearchFlow}
        />
      )}
    </>
  );
};
